import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import { Nav, Card, Form } from 'react-bootstrap';

import FilterProvider from 'providers/FilterProvider';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';

import useTableData from 'hooks/useTableData';
import useUrlParams from 'hooks/useUrlParams';
import useAdvanceTable from 'hooks/useAdvanceTable';
import useTableSelectBox from 'hooks/useTableSelectBox';
import useTableTabs from 'hooks/useTableTabs';
import useBulkActions from 'hooks/useBulkActions';
import useTableExport from 'hooks/useTableExport';

import TableHeader from 'components/common/Table/TableHeader';
import TableBody from 'components/common/Table/TableBody';
import TablePagination from 'components/common/Table/TablePagination';
import FilterPanel from 'components/filter-panel/FilterPanel';

const Table = forwardRef(
  (
    {
      title,
      optionalHeaderComponent,
      apiService,
      exportApi,
      exportButtonFirst = false,
      columns,
      isShowFilterButton = true,
      filterOptions,
      sortable = false,
      initialPageSize = 25,
      selectBoxActions,
      selectBoxOptions,
      tabOptions,
      headerActions,
      bulkActions,
      initialFilter,
      fetchConfig = {},
      enablePagination = true,
      renderExpandedRow,
      resourceId,
      onDataChange,
      tableConfig,
    },
    ref,
  ) => {
    const initialFilters = useMemo(() => {
      // Preset
      if (tabOptions?.isPreset && tabOptions.defaultTab) {
        const defaultPreset = tabOptions.tabs.find(
          tab => tab.key === tabOptions.defaultTab,
        );
        if (defaultPreset?.preset) {
          return defaultPreset.preset;
        }
      }

      // Not Preset
      const tabFilter = {};
      if (tabOptions?.tabs?.length && !tabOptions?.multiSelect) {
        const firstTab = tabOptions.tabs[0];
        if (firstTab.value !== undefined) {
          tabFilter[tabOptions.field] = firstTab.value;
        }
      }

      return {
        ...initialFilter,
        ...tabFilter,
      };
    }, [tabOptions, initialFilter]);

    const { pageIndex, pageSize, filters, sorting, updateParams } =
      useUrlParams(
        filterOptions,
        initialPageSize,
        initialFilters,
        enablePagination,
      );

    const { selectedValue, handleSelectChange } = useTableSelectBox({
      selectBoxOptions,
      pageSize,
      filters,
      sorting,
      updateParams,
    });

    const { activeTabKeys, handleTabSelect } = useTableTabs({
      tabOptions,
      pageSize,
      filters,
      sorting,
      updateParams,
    });

    const {
      data = [],
      totalItems = 0,
      loading,
      error,
      refetch,
    } = useTableData({
      apiService,
      method: fetchConfig.method || 'getList',
      queryParams: enablePagination
        ? { pageIndex, pageSize, filters, sorting }
        : { filters, sorting },
      resourceId,
      customConfig: {
        ...fetchConfig,
        enablePagination,
      },
    });

    const { exportButton } = useTableExport({
      exportApi,
      filters,
      sorting,
      filterOptions,
    });

    useEffect(() => {
      onDataChange?.(data, totalItems);
    }, [data, totalItems, onDataChange]);

    useImperativeHandle(
      ref,
      () => ({
        refetch,
      }),
      [refetch],
    );

    const handleFilterChange = useCallback(
      newFilters => {
        const trimmedFilters = {};

        if (tabOptions?.tabs?.length && !tabOptions?.isPreset) {
          const firstTab = tabOptions.tabs[0];
          if (firstTab.value !== undefined) {
            trimmedFilters[tabOptions.field] = firstTab.value;
          }
        }

        if (newFilters && Object.keys(newFilters).length > 0) {
          Object.entries(newFilters).forEach(([key, value]) => {
            if (typeof value === 'string') {
              trimmedFilters[key] = value.trim();
            } else {
              trimmedFilters[key] = value;
            }
          });
        }

        updateParams({
          pageIndex: 0,
          pageSize,
          filters: trimmedFilters,
          sorting,
        });
      },
      [tabOptions, pageSize, sorting, updateParams],
    );

    const handlePaginationChange = useCallback(
      ({ pageIndex: newPageIndex, pageSize: newPageSize }) => {
        updateParams({
          pageIndex: newPageIndex,
          pageSize: newPageSize,
          filters,
          sorting,
        });
      },
      [filters, sorting, updateParams],
    );

    const handleSortingChange = useCallback(
      updaterOrValue => {
        let newSorting = [];

        if (typeof updaterOrValue === 'function') {
          const existingSorting = sorting || [];
          const nextSorting = updaterOrValue(existingSorting);

          // Find changed column by comparing the arrays
          const changedColumn = nextSorting.find(
            sort =>
              !existingSorting.some(
                existing =>
                  existing.id === sort.id && existing.desc === sort.desc,
              ),
          );

          if (!changedColumn) {
            // If column was removed (third click), remove it from sorting
            const removedColumn = existingSorting.find(
              sort => !nextSorting.some(next => next.id === sort.id),
            );
            if (removedColumn) {
              newSorting = existingSorting.filter(
                sort => sort.id !== removedColumn.id,
              );
            } else {
              newSorting = existingSorting;
            }
          } else {
            // Column was clicked - either added or changed direction
            const existingColumnIndex = existingSorting.findIndex(
              sort => sort.id === changedColumn.id,
            );

            if (existingColumnIndex === -1) {
              // New column - add to sorting
              newSorting = [...existingSorting, changedColumn];
            } else {
              if (
                changedColumn.desc &&
                !existingSorting[existingColumnIndex].desc
              ) {
                // Second click - change to descending
                newSorting = existingSorting.map(sort =>
                  sort.id === changedColumn.id ? changedColumn : sort,
                );
              } else {
                // Third click - remove from sorting
                newSorting = existingSorting.filter(
                  sort => sort.id !== changedColumn.id,
                );
              }
            }
          }
        } else {
          newSorting = updaterOrValue;
        }

        updateParams({
          pageIndex,
          pageSize,
          filters,
          sorting: newSorting,
        });
      },
      [pageIndex, pageSize, filters, sorting, updateParams],
    );

    const table = useAdvanceTable({
      data: data || [],
      columns,
      // Sort
      sortable,
      sortingState: sorting,
      onSortingChange: handleSortingChange,
      // Pagination
      totalItems,
      enablePagination,
      paginationState: enablePagination ? { pageIndex, pageSize } : undefined,
      onPaginationChange: handlePaginationChange,
      // Selection/ Expander
      selection: !!bulkActions,
      expandable: !!renderExpandedRow,
      expandableColumnWidth: '40px',
      tableConfig,
    });

    const { isLoading: isBulkActionProcessing, executeBulkAction } =
      useBulkActions({
        table,
        bulkActions,
        onSuccess: async result => {
          await refetch();
        },
      });

    useEffect(() => {
      if (table?.getSelectedRowModel()?.rows?.length > 0) {
        table?.toggleAllRowsSelected(false);
      }
    }, [pageIndex, filters, table]);

    const headerActionElements = exportButtonFirst ? (
      <>
        {exportButton}
        {headerActions}
      </>
    ) : (
      <>
        {headerActions}
        {exportButton}
      </>
    );

    return (
      <FilterProvider filters={filters} onFilterChange={handleFilterChange}>
        <AdvanceTableProvider {...table}>
          {selectBoxOptions && (
            <div className="d-flex align-items-center gap-2 mt-1 mb-3">
              <Form.Select
                value={selectedValue}
                onChange={handleSelectChange}
                style={{ width: '250px' }}
              >
                {selectBoxOptions.options.map(option => (
                  <option key={option.key} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>

              {selectBoxActions}
            </div>
          )}

          {tabOptions && (
            <div className="overflow-x-auto">
              <Nav variant="tabs" className="mb-3 flex flex-row flex-nowrap">
                {tabOptions?.tabs?.map(tab => (
                  <Nav.Item key={tab.key} className="flex-shrink-0">
                    <Nav.Link
                      active={activeTabKeys.includes(tab.key)}
                      onClick={() => handleTabSelect(tab.key)}
                      className="cursor-pointer outline-none"
                    >
                      {tab.label}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            </div>
          )}

          <Card className="mb-3">
            <Card.Header>
              <TableHeader
                title={title}
                isShowFilterButton={isShowFilterButton && !!filterOptions}
                initialFilter={initialFilter}
                filterOptions={filterOptions}
                actions={headerActionElements}
                bulkActions={bulkActions}
                isBulkActionProcessing={isBulkActionProcessing}
                onBulkActionApply={executeBulkAction}
              />

              {optionalHeaderComponent}
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center p-4">Loading...</div>
              ) : error ? (
                <div className="text-center p-4 text-danger">{error}</div>
              ) : data.length === 0 ? (
                <div className="text-center p-4">No data available</div>
              ) : (
                <TableBody
                  headerClassName="bg-200 text-nowrap align-middle text-center"
                  rowClassName="align-middle white-space-nowrap text-center"
                  tableProps={{
                    size: 'sm',
                    striped: true,
                    className: 'fs-10 mb-0 overflow-hidden',
                  }}
                  renderExpandedRow={renderExpandedRow}
                />
              )}
            </Card.Body>
            {enablePagination && (
              <Card.Footer>
                <TablePagination />
              </Card.Footer>
            )}
          </Card>

          {filterOptions && (
            <FilterPanel
              initialFilter={initialFilter}
              filterOptions={filterOptions}
              pageSize={pageSize}
            />
          )}
        </AdvanceTableProvider>
      </FilterProvider>
    );
  },
);

export default Table;
